/**
 * Zevet Chat's thread state: transcript.mjs's reducer, plus the one thing a
 * chat needs that a console does not — token streaming.
 *
 * desktop/chat.js runs claude with --include-partial-messages, so text
 * arrives as `stream_event` deltas BEFORE the whole block lands as an
 * `assistant` payload. transcript.mjs drops stream_event on purpose (Code
 * never asks for them). Here they grow `draft`, a view-only overlay that the
 * complete block replaces, so nothing is ever counted twice.
 *
 * .mjs for the same reason as transcript.mjs: `node --test` runs it directly.
 */
import { appendAgentPayload, appendUserText, closeTranscript, emptyTranscript } from "./transcript.mjs";
import { usageOf } from "./usage.mjs";
import { runningModelName } from "./models.mjs";

/** @returns {import("./chat-stream.d.mts").ChatThread} */
export function emptyChatThread() {
  return { transcript: emptyTranscript(), draft: "", busy: false, usage: null, slashCommands: null, model: null, agent: "claude", route: "" };
}

/** A saved chat ({role, text}[]) as a closed thread. */
export function fromStored(messages) {
  let t = emptyTranscript();
  for (const m of messages || []) {
    if (!m || typeof m.text !== "string") continue;
    if (m.role === "user") t = appendUserText(t, m.text);
    else {
      t = appendAgentPayload(t, { type: "assistant", message: { content: [{ type: "text", text: m.text }] } });
      t = closeTranscript(t, { code: 0 });
    }
  }
  return { transcript: t, draft: "", busy: false, usage: null, slashCommands: null, model: null, agent: "claude", route: "" };
}

let turns = 0;

/** The person's words, shown the moment Send is pressed, and the reply's
 *  message opened under them. Opening it NOW keeps one id from the first
 *  streamed token to the last block: a draft message that the real one later
 *  replaced read to assistant-ui as a second branch ("2 / 2" under a reply
 *  that was only ever given once, seen in the running app).
 *
 *  `model` is what this turn was actually sent on (chat.ts reads it off
 *  useBoard's launchModel at Send time) — carried on the thread so a later
 *  event that closes the turn knows which model to grade a rate limit
 *  against, without re-reading a launch preference that may have moved on to
 *  a different model by the time the reply arrives. Never agent-qualified.
 *
 *  `agent` is the CLI the turn went to (claude|codex|opencode): each speaks its
 *  own JSONL, and it picks the vocabulary transcript.mjs reads it with. */
export function sendUser(thread, text, model, agent) {
  // The switch rule (transcript.mjs appendUserText) names the model this
  // prompt goes to: "Zevet" for the router, else the model's display name.
  const on = (agent || thread.agent) === "zevet" ? "Zevet" : model ? runningModelName("", model) : "";
  const t = appendUserText(thread.transcript, text, on);
  const messages = t.messages.concat({ id: `zc-${++turns}`, role: "assistant", content: [], status: { type: "running" } });
  return {
    ...thread,
    transcript: { ...t, messages, openIndex: messages.length - 1 },
    draft: "",
    busy: true,
    usage: thread.usage,
    slashCommands: thread.slashCommands,
    model: model ?? thread.model ?? null,
    agent: agent || thread.agent || "claude",
    route: "",
  };
}

/** One `chat:event` evt from the desktop side. */
export function chatEvent(thread, evt) {
  if (!evt || typeof evt !== "object") return thread;
  // A routed turn that ended on codex or opencode (zevet-router.js): no
  // process exit follows, so this is its end. Same close as a clean exit.
  if (evt.type === "turn_end") {
    return { ...thread, transcript: thread.busy ? closeTranscript(thread.transcript, { code: 0 }) : thread.transcript, draft: "", busy: false };
  }
  if (evt.type === "exit") {
    // Mid-turn and not stopped by the person: the process died before its
    // `result`. That lands on the thread even when nothing was said yet.
    const died = !evt.stopped && evt.code !== 0 ? evt.error || "The run stopped." : null;
    return {
      ...thread,
      transcript: thread.busy ? closeTranscript(thread.transcript, { ...evt, error: died }) : thread.transcript,
      draft: "",
      busy: false,
    };
  }
  if (evt.type !== "agent") return thread;
  const p = evt.payload;
  if (!p || typeof p !== "object") return thread;
  /* claude announces every command it will accept on the init line, the same
     way Code's consoles record it (lib/board.ts). Before any run the list is
     null and commandsFor falls back to CLAUDE_FALLBACK. */
  if (p.type === "system" && Array.isArray(p.slash_commands)) {
    return { ...thread, slashCommands: p.slash_commands.filter((n) => typeof n === "string") };
  }
  if (p.type === "stream_event") {
    const draft = draftAfter(thread.draft, p);
    return draft === thread.draft ? thread : { ...thread, draft };
  }
  /* A routed thread (agent "zevet") speaks several CLIs' dialects; the router
     tags each event with the one that produced it, as board.ts does for Code. */
  const route = p.type === "zevet_route" ? String(p.label || "") : thread.route;
  const routeWhy = p.type === "zevet_route" ? String(p.reason || "") : thread.routeWhy;
  const transcript = appendAgentPayload(thread.transcript, p, { agent: evt.agent || thread.agent, model: evt.model || thread.model || "" });
  let usage = thread.usage;
  const u = usageOf(p);
  if (u) usage = u;
  if (p.type === "result") return { ...thread, transcript, draft: "", busy: false, usage, route, routeWhy };
  if (p.type === "assistant") return { ...thread, transcript, draft: "", usage, route, routeWhy };
  /* `/clear` lands as `conversation_reset`: the transcript empties (transcript.mjs)
     and any streamed draft must go with it, or the screen keeps old tokens. */
  return transcript === thread.transcript && route === thread.route && routeWhy === thread.routeWhy ? thread : { ...thread, transcript, usage, draft: "", route, routeWhy };
}

/** A failure to even start the turn, drawn where the reply would be. */
export function failTurn(thread, error) {
  return { ...thread, transcript: closeTranscript(thread.transcript, { error }), draft: "", busy: false };
}

/** The draft after one agent payload, for a Code console as well as a chat:
 *  text deltas grow it, and the block that carries the same words whole (or the
 *  end of the turn, or `/clear`) replaces it. A subagent's tokens are not this
 *  thread's, so anything tagged with a parent tool call is left out. */
export function draftAfter(draft, p) {
  if (!p || typeof p !== "object" || p.parent_tool_use_id) return draft;
  if (p.type === "stream_event") {
    const d = p.event && p.event.type === "content_block_delta" ? p.event.delta : null;
    return d && d.type === "text_delta" && typeof d.text === "string" ? draft + d.text : draft;
  }
  return p.type === "assistant" || p.type === "result" || p.type === "conversation_reset" ? "" : draft;
}

/** What the runtime renders: the transcript with the draft laid over it. */
export function visibleMessages(thread) {
  return overlayDraft(thread.transcript, thread.draft);
}

export function overlayDraft(transcript, draft) {
  const { messages, openIndex } = transcript;
  if (!draft) return messages;
  const part = { type: "text", text: draft };
  if (openIndex >= 0) {
    const out = messages.slice();
    const open = out[openIndex];
    out[openIndex] = { ...open, content: [...open.content, part] };
    return out;
  }
  return messages.concat({ id: "zv-draft", role: "assistant", content: [part], status: { type: "running" } });
}
